import { useEffect, useState, useCallback } from "react";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type SeedBotOrg = {
  id: string;
  organizationId: string;
  personality: string;
  region: string | null;
  active: number;
  organization: { id: string; name: string; slug: string };
};

type AuctionConfig = {
  config: {
    id: string;
    auctionId: string;
    targetPrice: number | null;
    activityLevel: number;
    paused: number;
    selectedOrgIds: string[];
    createdAt: string;
    updatedAt: string;
  };
  auctionTitle: string | null;
  auctionStatus: string | null;
  currentBid: number;
  bidCount: number;
  endsAt: string | null;
};

type AvailableAuction = {
  id: string;
  title: string | null;
  status: string | null;
  referenceNumber: string;
  currentBid: number;
  startsAt: string | null;
  endsAt: string | null;
};

type LogEntry = {
  id: string;
  auctionId: string;
  response: { reasoning?: string; actions?: { organizationId: string; action: string; amount?: number; delayMinutes?: number }[] } | null;
  actionsScheduled: number;
  createdAt: string;
};

type Dashboard = {
  auctions: AuctionConfig[];
  orgs: { botOrg: SeedBotOrg; orgName: string }[];
  recentLogs: LogEntry[];
};

async function apiFetch(path: string, options?: RequestInit) {
  return fetch(`${API}/api/admin/seed-bot${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

function formatNok(amount: number) {
  return new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(amount);
}

function formatTime(iso: string | null) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function timeRemaining(endsAt: string | null) {
  if (!endsAt) return "-";
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "Avsluttet";
  const hours = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}t ${mins}m` : `${mins}m`;
}

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  pending_review: "Til godkjenning",
  approved: "Godkjent",
  scheduled: "Planlagt",
  live: "Aktiv",
  pending_seller_decision: "Venter selger",
  sold: "Solgt",
  no_sale: "Ikke solgt",
};

const personalityLabels: Record<string, { label: string; color: string }> = {
  aggressive: { label: "Aggressiv", color: "bg-red-100 text-red-800" },
  conservative: { label: "Konservativ", color: "bg-blue-100 text-blue-800" },
  sniper: { label: "Sniper", color: "bg-purple-100 text-purple-800" },
  steady: { label: "Jevn", color: "bg-green-100 text-green-800" },
  selective: { label: "Selektiv", color: "bg-orange-100 text-orange-800" },
  budget: { label: "Budsjett", color: "bg-yellow-100 text-yellow-800" },
  premium: { label: "Premium", color: "bg-indigo-100 text-indigo-800" },
  casual: { label: "Tilfeldig", color: "bg-gray-100 text-gray-700" },
};

export function SeedBotPage() {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [orgs, setOrgs] = useState<SeedBotOrg[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [availableAuctions, setAvailableAuctions] = useState<AvailableAuction[]>([]);
  const [tab, setTab] = useState<"dashboard" | "orgs" | "logs" | "prompt">("dashboard");
  const [loading, setLoading] = useState(true);

  // Enable bot form
  const [enableAuctionId, setEnableAuctionId] = useState("");
  const [enableTargetPrice, setEnableTargetPrice] = useState("");
  const [enableActivityLevel, setEnableActivityLevel] = useState(5);

  // Manual bid form
  const [manualAuctionId, setManualAuctionId] = useState("");
  const [manualOrgId, setManualOrgId] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualResult, setManualResult] = useState<string | null>(null);
  const [stealthMode, setStealthMode] = useState(false);
  const [liveAuctions, setLiveAuctions] = useState<AvailableAuction[]>([]);

  // Prompt editor
  const [promptText, setPromptText] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [promptIsCustom, setPromptIsCustom] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);

  // Log expansion
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await apiFetch("/dashboard");
      setDashboard(await res.json());
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    const res = await apiFetch("/orgs");
    setOrgs(await res.json());
  }, []);

  const fetchLogs = useCallback(async () => {
    const res = await apiFetch("/logs?limit=50");
    setLogs(await res.json());
  }, []);

  const fetchPrompt = useCallback(async () => {
    const res = await apiFetch("/prompt");
    const data = await res.json();
    setDefaultPrompt(data.defaultPrompt);
    if (data.systemPrompt) {
      setPromptText(data.systemPrompt);
      setPromptIsCustom(true);
    } else {
      setPromptText(data.defaultPrompt);
      setPromptIsCustom(false);
    }
  }, []);

  const fetchAvailableAuctions = useCallback(async () => {
    const res = await apiFetch("/available-auctions");
    setAvailableAuctions(await res.json());
  }, []);

  const fetchLiveAuctions = useCallback(async () => {
    const res = await apiFetch("/live-auctions");
    setLiveAuctions(await res.json());
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchOrgs();
    fetchLogs();
    fetchAvailableAuctions();
    fetchLiveAuctions();
    fetchPrompt();
    const interval = setInterval(() => {
      fetchDashboard();
      fetchAvailableAuctions();
      fetchLiveAuctions();
    }, 30_000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchOrgs, fetchLogs, fetchAvailableAuctions, fetchPrompt]);

  async function enableBot() {
    await apiFetch(`/auctions/${enableAuctionId}/enable`, {
      method: "POST",
      body: JSON.stringify({
        targetPrice: enableTargetPrice ? Number(enableTargetPrice) : null,
        activityLevel: enableActivityLevel,
      }),
    });
    setEnableAuctionId("");
    setEnableTargetPrice("");
    fetchDashboard();
    fetchAvailableAuctions();
  }

  async function togglePause(auctionId: string, paused: number) {
    await apiFetch(`/auctions/${auctionId}/${paused ? "resume" : "pause"}`, { method: "POST" });
    fetchDashboard();
  }

  async function updateActivityLevel(auctionId: string, level: number) {
    await apiFetch(`/auctions/${auctionId}/config`, {
      method: "PATCH",
      body: JSON.stringify({ activityLevel: level }),
    });
    fetchDashboard();
  }

  async function deleteAuction(auctionId: string) {
    if (!confirm("Slett auksjonen og alle relaterte data?")) return;
    await apiFetch(`/auctions/${auctionId}`, { method: "DELETE" });
    fetchDashboard();
    fetchAvailableAuctions();
  }

  async function disableBot(auctionId: string) {
    await apiFetch(`/auctions/${auctionId}/disable`, { method: "POST" });
    fetchDashboard();
    fetchAvailableAuctions();
  }

  async function placeBid() {
    setManualResult(null);
    const endpoint = stealthMode ? "stealth-bid" : "manual-bid";
    const res = await apiFetch(`/auctions/${manualAuctionId}/${endpoint}`, {
      method: "POST",
      body: JSON.stringify({
        organizationId: manualOrgId,
        amount: Number(manualAmount),
      }),
    });
    const data = await res.json();
    setManualResult(data.ok ? "Bud plassert!" : JSON.stringify(data));
    setManualAmount("");
    fetchDashboard();
    fetchLiveAuctions();
  }

  async function savePrompt() {
    setPromptSaving(true);
    await apiFetch("/prompt", {
      method: "PUT",
      body: JSON.stringify({ systemPrompt: promptText }),
    });
    setPromptIsCustom(true);
    setPromptSaving(false);
    setPromptSaved(true);
    setTimeout(() => setPromptSaved(false), 2000);
  }

  async function resetPrompt() {
    setPromptSaving(true);
    await apiFetch("/prompt", {
      method: "PUT",
      body: JSON.stringify({ systemPrompt: null }),
    });
    setPromptText(defaultPrompt);
    setPromptIsCustom(false);
    setPromptSaving(false);
    setPromptSaved(true);
    setTimeout(() => setPromptSaved(false), 2000);
  }

  async function triggerPoll() {
    await apiFetch("/trigger-poll", { method: "POST" });
    setTimeout(() => {
      fetchDashboard();
      fetchLogs();
    }, 2000);
  }

  async function bulkCleanup() {
    if (!confirm("Slett alle avsluttede seeded auksjoner?")) return;
    const res = await apiFetch("/cleanup", { method: "POST" });
    const data = await res.json();
    alert(`Slettet ${data.deleted} auksjoner`);
    fetchDashboard();
    fetchAvailableAuctions();
  }

  async function toggleOrg(id: string, active: number) {
    await apiFetch(`/orgs/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ active: active ? 0 : 1 }),
    });
    fetchOrgs();
  }

  function getOrgName(orgId: string) {
    return orgs.find((o) => o.organizationId === orgId)?.organization.name ?? orgId.slice(0, 8);
  }

  // Active seeded auction IDs for the manual bid dropdown
  const seededAuctionIds = dashboard?.auctions.map((a) => a.config.auctionId) ?? [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Laster...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] sm:h-screen">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h1 className="text-lg font-semibold">Seed Bot</h1>
          <p className="text-sm text-muted-foreground">
            {dashboard?.auctions.filter((a) => a.auctionStatus === "live" && !a.config.paused).length ?? 0} aktive auksjoner
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={triggerPoll} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90">
            Trigger poll
          </button>
          <button onClick={bulkCleanup} className="px-3 py-1.5 text-sm bg-destructive text-destructive-foreground rounded-md hover:opacity-90">
            Rydd opp
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-3 border-b border-border">
        {(["dashboard", "orgs", "logs", "prompt"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "dashboard" ? "Auksjoner" : t === "orgs" ? `Organisasjoner (${orgs.length})` : t === "logs" ? "AI-logger" : "Prompt"}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {tab === "dashboard" && (
          <>
            {/* Enable bot form */}
            <div className="border border-border rounded-lg p-4 space-y-4">
              <h2 className="font-medium">Aktiver bot for auksjon</h2>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground block mb-1">Velg auksjon</label>
                  <select
                    value={enableAuctionId}
                    onChange={(e) => setEnableAuctionId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                  >
                    <option value="">Velg auksjon...</option>
                    {availableAuctions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.referenceNumber} — {a.title ?? "Uten tittel"} ({statusLabels[a.status ?? ""] ?? a.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Målpris (NOK)</label>
                  <input
                    value={enableTargetPrice}
                    onChange={(e) => setEnableTargetPrice(e.target.value)}
                    type="number"
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                    placeholder="Valgfri"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Aktivitet: {enableActivityLevel}/10</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={enableActivityLevel}
                      onChange={(e) => setEnableActivityLevel(Number(e.target.value))}
                      className="flex-1"
                    />
                    <button
                      onClick={enableBot}
                      disabled={!enableAuctionId}
                      className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                    >
                      Aktiver
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active seeded auctions */}
            <div className="space-y-3">
              <h2 className="font-medium">Bot-styrte auksjoner ({dashboard?.auctions.length ?? 0})</h2>
              {dashboard?.auctions.length === 0 && (
                <p className="text-sm text-muted-foreground py-8 text-center">Ingen auksjoner med bot aktivert</p>
              )}
              {dashboard?.auctions.map((a) => {
                const selectedOrgIds = a.config.selectedOrgIds as string[];
                return (
                  <div key={a.config.id} className="border border-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{a.auctionTitle ?? "Uten tittel"}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            a.config.paused ? "bg-yellow-100 text-yellow-800" : a.auctionStatus === "live" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                          }`}>
                            {a.config.paused ? "Pauset" : statusLabels[a.auctionStatus ?? ""] ?? a.auctionStatus}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm text-muted-foreground">
                          <span>Bud: <strong className="text-foreground">{formatNok(a.currentBid)}</strong></span>
                          <span>{a.bidCount} bud</span>
                          <span>Tid igjen: {timeRemaining(a.endsAt)}</span>
                          {a.config.targetPrice && <span>Mål: {formatNok(a.config.targetPrice)}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Selected orgs */}
                    <div className="flex flex-wrap gap-1.5">
                      {selectedOrgIds.map((orgId) => {
                        const org = orgs.find((o) => o.organizationId === orgId);
                        const p = org ? personalityLabels[org.personality] : null;
                        return (
                          <span key={orgId} className={`text-xs px-2 py-0.5 rounded-full ${p?.color ?? "bg-muted"}`}>
                            {org?.organization.name?.replace(/ AS$/, "") ?? orgId.slice(0, 8)}
                          </span>
                        );
                      })}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-3 pt-1 border-t border-border">
                      <div className="flex items-center gap-2 flex-1">
                        <label className="text-xs text-muted-foreground whitespace-nowrap">Aktivitet: {a.config.activityLevel}/10</label>
                        <input
                          type="range"
                          min={1}
                          max={10}
                          value={a.config.activityLevel}
                          onChange={(e) => updateActivityLevel(a.config.auctionId, Number(e.target.value))}
                          className="w-32"
                        />
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => togglePause(a.config.auctionId, a.config.paused)} className="px-3 py-1 text-xs font-medium border border-input rounded-md hover:bg-muted">
                          {a.config.paused ? "Start" : "Pause"}
                        </button>
                        <button onClick={() => disableBot(a.config.auctionId)} className="px-3 py-1 text-xs border border-input rounded-md hover:bg-muted">
                          Fjern bot
                        </button>
                        <button onClick={() => deleteAuction(a.config.auctionId)} className="px-3 py-1 text-xs text-destructive border border-destructive/30 rounded-md hover:bg-destructive/10">
                          Slett auksjon
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Manual bid */}
            <div className="border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-medium">Manuelt bud</h2>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stealthMode}
                    onChange={(e) => { setStealthMode(e.target.checked); setManualAuctionId(""); }}
                    className="rounded"
                  />
                  <span className={stealthMode ? "text-green-600 font-medium" : "text-muted-foreground"}>
                    Stealth {stealthMode ? "(ingen sporing)" : ""}
                  </span>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Auksjon {stealthMode ? "(alle aktive)" : "(seeded)"}</label>
                  <select
                    value={manualAuctionId}
                    onChange={(e) => setManualAuctionId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                  >
                    <option value="">Velg auksjon...</option>
                    {stealthMode
                      ? liveAuctions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.title ?? a.referenceNumber}
                          </option>
                        ))
                      : dashboard?.auctions.map((a) => (
                          <option key={a.config.auctionId} value={a.config.auctionId}>
                            {a.auctionTitle ?? a.config.auctionId.slice(0, 8)}
                          </option>
                        ))
                    }
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Organisasjon</label>
                  <select
                    value={manualOrgId}
                    onChange={(e) => setManualOrgId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                  >
                    <option value="">Velg org...</option>
                    {orgs.filter((o) => o.active).map((o) => (
                      <option key={o.id} value={o.organizationId}>
                        {o.organization.name.replace(/ AS$/, "")} ({personalityLabels[o.personality]?.label ?? o.personality})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Beløp (NOK)</label>
                  <input
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                    type="number"
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background"
                    placeholder="f.eks. 195000"
                  />
                </div>
                <button
                  onClick={placeBid}
                  disabled={!manualAuctionId || !manualOrgId || !manualAmount}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  Legg inn bud
                </button>
              </div>
              {manualResult && (
                <p className={`text-sm ${manualResult === "Bud plassert!" ? "text-green-600" : "text-destructive"}`}>
                  {manualResult}
                </p>
              )}
            </div>
          </>
        )}

        {tab === "orgs" && (
          <div className="space-y-3">
            <h2 className="font-medium">Interne organisasjoner</h2>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-medium">Navn</th>
                    <th className="text-left px-4 py-2.5 font-medium">Region</th>
                    <th className="text-left px-4 py-2.5 font-medium">Personlighet</th>
                    <th className="text-left px-4 py-2.5 font-medium">Status</th>
                    <th className="text-right px-4 py-2.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => {
                    const p = personalityLabels[o.personality];
                    return (
                      <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-2.5 font-medium">{o.organization.name}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{o.region ?? "-"}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${p?.color ?? "bg-muted"}`}>
                            {p?.label ?? o.personality}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${o.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                            {o.active ? "Aktiv" : "Inaktiv"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            onClick={() => toggleOrg(o.id, o.active)}
                            className="text-xs px-3 py-1 border border-input rounded-md hover:bg-muted"
                          >
                            {o.active ? "Deaktiver" : "Aktiver"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "prompt" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-medium">System Prompt</h2>
                <p className="text-sm text-muted-foreground">
                  {promptIsCustom ? "Bruker tilpasset prompt" : "Bruker standard prompt"} — endringer gjelder fra neste poll-syklus
                </p>
              </div>
              <div className="flex gap-2 items-center">
                {promptSaved && <span className="text-sm text-green-600">Lagret!</span>}
                {promptIsCustom && (
                  <button
                    onClick={resetPrompt}
                    disabled={promptSaving}
                    className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted disabled:opacity-50"
                  >
                    Tilbakestill
                  </button>
                )}
                <button
                  onClick={savePrompt}
                  disabled={promptSaving}
                  className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  {promptSaving ? "Lagrer..." : "Lagre"}
                </button>
              </div>
            </div>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className="w-full h-[calc(100vh-280px)] px-4 py-3 text-sm font-mono border border-input rounded-lg bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              spellCheck={false}
            />
          </div>
        )}

        {tab === "logs" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="font-medium">AI-beslutninger</h2>
              <button onClick={fetchLogs} className="text-xs px-3 py-1 border border-input rounded-md hover:bg-muted">
                Oppdater
              </button>
            </div>
            {logs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Ingen logger ennå</p>}
            {logs.map((log) => {
              const isExpanded = expandedLogId === log.id;
              const actions = log.response?.actions ?? [];
              return (
                <div
                  key={log.id}
                  className="border border-border rounded-lg overflow-hidden cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                >
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-muted-foreground">{log.auctionId.slice(0, 8)}</span>
                      <span className="text-sm">{log.actionsScheduled} handlinger</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatTime(log.createdAt)}</span>
                  </div>
                  {log.response?.reasoning && (
                    <div className="px-4 pb-3 -mt-1">
                      <p className="text-sm text-muted-foreground">{log.response.reasoning}</p>
                    </div>
                  )}
                  {isExpanded && actions.length > 0 && (
                    <div className="border-t border-border bg-muted/30 px-4 py-3 space-y-1.5">
                      {actions.map((action, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded font-mono ${
                            action.action === "bid" ? "bg-green-100 text-green-800"
                              : action.action === "set_auto_bid" ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            {action.action}
                          </span>
                          <span className="text-muted-foreground">{getOrgName(action.organizationId)}</span>
                          {action.amount && <span className="font-medium">{formatNok(action.amount)}</span>}
                          {action.delayMinutes != null && <span className="text-muted-foreground">om {action.delayMinutes}m</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
